/**
 * Textarea — shadcn Textarea 어댑터
 *
 * 기존 @/ui Textarea API(label/error/helperText/size)를 유지하고
 * 내부 렌더링은 shadcn Textarea에 위임한다.
 * forwardRef로 외부에서 ref 접근을 지원한다.
 */

import { forwardRef } from 'react'
import type { TextareaHTMLAttributes } from 'react'
import { Textarea as ShadTextarea } from '@/components/ui/textarea'
import { cn } from './cn'

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** 입력 크기. 기본값 md */
  size?: 'sm' | 'md' | 'lg'
  /** 필드 레이블 텍스트 */
  label?: string
  /** 에러 메시지 */
  error?: string
  /** 보조 안내 텍스트 */
  helperText?: string
}

// size prop → 최소 높이 클래스 매핑
const sizeClass: Record<NonNullable<TextareaProps['size']>, string> = {
  sm: 'min-h-[60px]',
  md: 'min-h-[80px]',
  lg: 'min-h-[120px]',
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { size = 'md', label, error, helperText, className, id, ...props },
  ref,
) {
  const textareaId =
    id ?? (label ? `textarea-${Math.random().toString(36).slice(2, 9)}` : undefined)

  // className은 기존 계약대로 외부 wrapper div에 적용 (레이블+textarea+헬퍼 그룹 전체 여백 제어)
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label className="text-sm font-medium text-foreground" htmlFor={textareaId}>
          {label}
        </label>
      )}
      <ShadTextarea
        ref={ref}
        id={textareaId}
        // aria-invalid 으로 shadcn 내장 에러 스타일(border-destructive) 활성화
        aria-invalid={!!error || undefined}
        className={sizeClass[size]}
        {...props}
      />
      {error && <span className="text-xs text-destructive">{error}</span>}
      {!error && helperText && <span className="text-xs text-muted-foreground">{helperText}</span>}
    </div>
  )
})
